import './styles.scss';
import React from "react";
import { Button, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, DialogTrigger } from "@fluentui/react-components";
import { QRCodeSVG } from "qrcode.react";
import * as ReactDOM from "react-dom/client";
import { showToast } from "#root/components/GlobalToaster";
import { Incident } from "#root/queries/incidents";
import { Copy24Filled, Dismiss20Regular, QrCode24Filled } from "@fluentui/react-icons";

interface QRCodeDialogProps {
  incident: Incident | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  qrUrl: string; 
}

 export const QRCodeDialog: React.FC<QRCodeDialogProps> = ({ incident, open, onOpenChange, qrUrl }) => {

  const generateQRCanvas = async (value: string, size: number): Promise<HTMLCanvasElement> => {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get canvas context");

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, size, size);

    const container = document.createElement("div");
    container.style.cssText = "position: absolute; left: -9999px; top: -9999px;";
    document.body.appendChild(container);

    const root = ReactDOM.createRoot(container);
    root.render(
      <QRCodeSVG
        value={value}
        size={size}
        level="H"
        bgColor="#FFFFFF"
        fgColor="#000000"
        marginSize={3}
      />
    );

    try {
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      const svg = container.querySelector("svg");
      if (!svg) throw new Error("QR code SVG not found");

      const svgString = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([svgString], { type: "image/svg+xml" });
      const url = URL.createObjectURL(svgBlob);

      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(url);
          resolve();
        };
        img.onerror = reject;
        img.src = url;
      });

      return canvas;
    } finally {
      root.unmount();
      document.body.removeChild(container);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(qrUrl);
    showToast("Link copied to clipboard", "success");
  };

  const handleShareQR = async () => {
    if (!incident?.id || !incident?.name) return;

    try {
      const canvas = await generateQRCanvas(qrUrl, 256);

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png", 1.0)
      );
      if (!blob) throw new Error("Failed to generate QR code blob");

      const filename = `incident_${incident.name}_qr.png`;
      //const file = new File([blob], filename, { type: "image/png" });

      // Save file to device
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = filename;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);

      /* Commenting out share functionality for now
      const shareData = {
        title: `EMC: Incident ${incident.name} at ${incident.address}`,
        text: `Scan this QR code to access incident: ${incident.name}`,
        url: qrUrl
      };

      const canShare =
        "share" in navigator &&
        "canShare" in navigator &&
        navigator.canShare({ files: [file] });

      if (!canShare) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = filename;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        return;
      }

      try {
        await navigator.share({ ...shareData, files: [file] });
      } catch (fileError) {
        console.error("Share with file failed, trying without:", fileError);
        await navigator.share(shareData);
      }
      */
    } catch (error) {
      // Only show error if it's not a user cancellation
      if (error instanceof Error && !error.message.includes('cancel')) {
        console.error("Error generating/saving QR code:", error);
        showToast("Failed to save QR code", "error");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface className="qr-dialog">
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" icon={<Dismiss20Regular />} />
              </DialogTrigger>
            }
          >
            Share QR Code
          </DialogTitle>
          <DialogContent>
            <div className="qr-code-container">
              <QRCodeSVG
                value={qrUrl}
                size={256}
                level="H"
                bgColor="var(--colorBrandBackground)"
                fgColor="var(--colorNeutralForegroundOnBrand)"
              />
            </div>
            <div className="incident-title">{incident?.name}</div>
          </DialogContent>
          <DialogActions className="dialog-actions-stretch">
            <Button appearance="primary" onClick={handleShareQR} icon={<QrCode24Filled />}>
              Save QR
            </Button>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={handleCopyLink} icon={<Copy24Filled />}>
                Copy Link
              </Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default QRCodeDialog;
